import React, { useState, useEffect } from 'react';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import BucketDeleteConfirmation from './BucketDeleteConfirmation';
import PaginatedTable from './PaginatedTable';
import { useCache } from '../hooks/useCache';

interface Bucket {
  bucketName: string;
  description: string;
  createdAt: string;
  imported?: boolean;
}

const BucketManagement: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingBucket, setEditingBucket] = useState<Bucket | null>(null);
  const [newBucket, setNewBucket] = useState({ bucketName: '', description: '' });
  const [deletingBucket, setDeletingBucket] = useState<string | null>(null);

  // Use cache for buckets data
  const { 
    data: buckets = [], 
    loading, 
    refresh: refreshBuckets 
  } = useCache(
    'admin-buckets',
    async () => {
      const response = await apiCall('/admin/buckets');
      return response;
    },
    { ttl: 60 * 1000 } // 1 minute cache for bucket data
  );

  const createBucket = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiCall('/admin/buckets', {
        method: 'POST',
        body: JSON.stringify(newBucket)
      });
      
      toast.success('Bucket created successfully');
      setNewBucket({ bucketName: '', description: '' });
      setShowCreateForm(false);
      refreshBuckets();
    } catch (error) {
      console.error('Error creating bucket:', error);
      toast.error('Failed to create bucket');
    }
  };

  const updateBucket = async (bucketName: string, description: string) => {
    try {
      await apiCall(`/admin/buckets/${bucketName}`, {
        method: 'PUT',
        body: JSON.stringify({ description })
      });
      
      toast.success('Bucket updated successfully');
      setEditingBucket(null);
      refreshBuckets();
    } catch (error) {
      console.error('Error updating bucket:', error);
      toast.error('Failed to update bucket');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingBucket) return;
    
    try {
      await apiCall(`/admin/buckets/${deletingBucket}`, {
        method: 'DELETE'
      });
      
      toast.success('Bucket deleted successfully');
      setDeletingBucket(null);
      refreshBuckets();
    } catch (error) {
      console.error('Error deleting bucket:', error);
      toast.error('Failed to delete bucket');
      setDeletingBucket(null);
    }
  };

  const getBucketAnalytics = async (bucketName: string) => {
    try {
      const response = await apiCall(`/admin/bucket-analytics/${bucketName}`);
      toast.success(`${bucketName}: ${response.fileCount} files, ${(response.totalSize / (1024*1024)).toFixed(2)} MB total`);
    } catch (error) {
      toast.error('Failed to get bucket analytics');
    }
  };

  const updateBucketCors = async (bucketName: string) => {
    try {
      const response = await apiCall(`/admin/bucket-cors/${bucketName}`, {
        method: 'PUT'
      });
      toast.success(`CORS configuration updated for "${bucketName}"`);
    } catch (error) {
      console.error('Error updating CORS:', error);
      toast.error(`Failed to update CORS for "${bucketName}"`);
    }
  };

  const columns = [
    {
      key: 'bucketName',
      header: 'Bucket Name',
      sortable: true,
      render: (bucket: Bucket) => (
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-900">{bucket.bucketName}</span>
          {bucket.imported && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
              Imported
            </span>
          )}
        </div>
      )
    },
    {
      key: 'description',
      header: 'Description',
      render: (bucket: Bucket) => (
        editingBucket?.bucketName === bucket.bucketName ? (
          <textarea
            value={editingBucket.description}
            onChange={(e) => setEditingBucket({ ...editingBucket, description: e.target.value })}
            className="w-full border-gray-300 rounded-md text-sm p-2"
            rows={2}
            placeholder="Enter description..."
          />
        ) : (
          <span className="text-sm text-gray-600">
            {bucket.description || 'No description'}
          </span>
        )
      )
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (bucket: Bucket) => (
        <span className="text-sm text-gray-500">
          {new Date(bucket.createdAt).toLocaleDateString()}
        </span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Bucket Management</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          {showCreateForm ? 'Cancel' : 'Add Bucket'}
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Bucket</h3>
          <form onSubmit={createBucket} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bucket Name</label>
              <input
                type="text"
                value={newBucket.bucketName}
                onChange={(e) => setNewBucket({ ...newBucket, bucketName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="my-s3-bucket"
                pattern="[a-z0-9.-]+"
                title="Bucket name must contain only lowercase letters, numbers, dots, and hyphens"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Must be globally unique and follow S3 naming rules</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newBucket.description}
                onChange={(e) => setNewBucket({ ...newBucket, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Optional description for this bucket"
              />
            </div>
            
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Create Bucket
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <PaginatedTable
        data={buckets || []}
        columns={columns}
        loading={loading}
        pageSize={10}
        searchPlaceholder="Search buckets by name, description..."
        emptyMessage="No buckets found"
        actions={(bucket: Bucket) => (
          <div className="flex items-center space-x-2">
            {editingBucket?.bucketName === bucket.bucketName ? (
              <>
                <button
                  onClick={() => updateBucket(bucket.bucketName, editingBucket.description)}
                  className="text-green-600 hover:text-green-800 text-sm font-medium"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingBucket(null)}
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => getBucketAnalytics(bucket.bucketName)}
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                >
                  Analytics
                </button>
                <button
                  onClick={() => updateBucketCors(bucket.bucketName)}
                  className="text-green-600 hover:text-green-800 text-sm font-medium"
                  title="Update CORS configuration for current domain"
                >
                  Update CORS
                </button>
                <button
                  onClick={() => setEditingBucket(bucket)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => setDeletingBucket(bucket.bucketName)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        )}
      />

      <BucketDeleteConfirmation
        bucketName={deletingBucket || ''}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingBucket(null)}
        isOpen={!!deletingBucket}
      />
    </div>
  );
};

export default BucketManagement;